import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ProductCard from "./product-card";
import IntroModule from "./molecules/intro-module";
import { A11y, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useInView } from "react-intersection-observer";
import { productImpression } from "../utility/data-layer";
import useProductPrices from "../utility/use-product-prices";
import CompareContext from "./context/compare";
import useReloadChannelSight from "../hooks/use-reload-channel-sight";
import ChevronRight from "../icons/chevron-right-gallery.svg";
import ChevronLeft from "../icons/chevron-left-gallery.svg";
import classNames from "classnames";

const defaultBreakpoints = {
  375: {
    slidesPerView: 1,
  },
  770: {
    slidesPerView: 3,
  },
  1024: {
    slidesPerView: 4,
  },
};

const SwiperPrevBtn = forwardRef(({ swiper, hideRightArrow, hideLeftArrow }, ref) => {
  return (
    <button
      ref={ref}
      className={classNames("swiper-button-prev swiper-button-prev--gallery", {
        "d-none": !swiper,
        "swiper-button-lock": hideRightArrow && hideLeftArrow,
        "swiper-button-disabled": hideLeftArrow,
      })}
      onClick={() => swiper?.slidePrev()}
      aria-label="prev"
    >
      <ChevronLeft />
    </button>
  );
});

const SwiperNextBtn = forwardRef(({ swiper, hideRightArrow, hideLeftArrow }, ref) => {
  return (
    <button
      ref={ref}
      className={classNames("swiper-button-next swiper-button-next--gallery", {
        "d-none": !swiper,
        "swiper-button-lock": hideRightArrow && hideLeftArrow,
        "swiper-button-disabled": hideRightArrow,
      })}
      onClick={() => {
        swiper?.slideNext();
      }}
      aria-label="next"
    >
      <ChevronRight />
    </button>
  );
});

const SliderRelated = ({
  data: prodotti,
  labelManagerLabels,
  location,
  madeProductLineId,
  badge,
  bannerPromo,
  liferayProductNotes,
  extraServices,
  isCartModal,
  onHide,
  isAcCompare,
}) => {
  const [prodottiItems, setProdottiItems] = useState([]);
  const { productIds: compareIds, toggleProduct } = useContext(CompareContext);
  const onToggleProduct = useCallback((e) => toggleProduct(e.currentTarget.name), [toggleProduct]);
  const productIds = useMemo(() => prodotti && prodotti.map((item) => item.key_prod), [prodotti]);
  const priceInfo = useProductPrices(productIds, prodotti);
  const [swiperData, setSwiperData] = useState(null);
  const [hideRightArrow, setHideRightArrow] = useState(false);
  const [hideLeftArrow, setHideLeftArrow] = useState(false);
  const [, setActiveIndex] = useState();
  const prevRef = useRef();
  const nextRef = useRef();

  useEffect(() => {
    if (priceInfo && prodotti) {
      const productsWithPriceInfo = prodotti.map((prodotto) => {
        const priceData = priceInfo.get(prodotto.key_prod);
        return {
          ...prodotto,
          priceData,
          shopLink: priceData?.shopLink ? priceData.shopLink : prodotto.shopLink,
          omnibusPrice: priceData?.omnibusPrice?.[0]?.value ? priceData.omnibusPrice : null,
        };
      });
      // Sort by in-stock first, discounted first
      productsWithPriceInfo.sort((a, b) => {
        const aInStock = !!a.priceData?.sellingPrice?.value;
        const bInStock = !!b.priceData?.sellingPrice?.value;
        const aCurrentPrice = a.priceData?.sellingPrice?.value;
        const bCurrentPrice = b.priceData?.sellingPrice?.value;

        if (aInStock === bInStock && !isAcCompare) {
          return aCurrentPrice > bCurrentPrice ? -1 : 1;
        }
        if (aInStock === bInStock && isAcCompare) {
          return aCurrentPrice === bCurrentPrice ? 0 : aCurrentPrice > bCurrentPrice ? -1 : 1;
        }
        return aInStock ? -1 : 1;
      });
      // Cap result at 9
      if (isAcCompare) {
        setProdottiItems(productsWithPriceInfo);
      } else {
        setProdottiItems(productsWithPriceInfo.slice(0, 9));
      }
    } else {
      setProdottiItems(prodotti);
    }
  }, [priceInfo, prodotti, isAcCompare]);

  useReloadChannelSight(priceInfo);

  const sliderRef = useRef();
  const setSlider = useCallback((slider) => (sliderRef.current = slider), []);

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    // Call product impressions when prices are loaded AND the products are in view
    if (inView && priceInfo) {
      productImpression(prodottiItems, origin);
    }
  }, [prodottiItems, inView, priceInfo]);

  const prodottiItemsWithPrice = useMemo(() => {
    return prodottiItems?.filter((item) => item?.priceData?.sellingPrice);
  }, [prodottiItems]);

  const productsFiltered = useMemo(() => {
    return isCartModal ? prodottiItemsWithPrice?.slice(0, 15) : prodottiItems;
  }, [isCartModal, prodottiItemsWithPrice, prodottiItems]);

  const prodottiItemsSorted = useMemo(() => {
    if (!isAcCompare) {
      return productsFiltered;
    } else {
      const productsBySkuWithPrice = productsFiltered?.filter(
        (product) => product?.priceData && product?.isBySku
      );
      const productsByLineWithPrice = productsFiltered?.filter(
        (product) => product?.priceData && !product?.isBySku
      );
      const productsBySkuWithoutPrice = productsFiltered?.filter(
        (product) => !product?.priceData && product?.isBySku
      );
      const productsByLineWithoutPrice = productsFiltered?.filter(
        (product) => !product?.priceData && !product?.isBySku
      );
      return productsBySkuWithPrice?.concat(
        productsByLineWithPrice,
        productsBySkuWithoutPrice,
        productsByLineWithoutPrice
      );
    }
  }, [productsFiltered, isAcCompare]);

  return (
    <>
      {prodottiItemsSorted?.length > 0 && (
        <div className="pdpSliderBottom__wrapper" ref={ref}>
          {isCartModal ? (
            <IntroModule title={labelManagerLabels.cartModalRelatedProducts} isCartModal />
          ) : isAcCompare ? (
            <IntroModule
              className="pdpSliderBottom"
              title={labelManagerLabels.accessoriesConsumables}
            />
          ) : (
            <IntroModule
              className="pdpSliderBottom"
              title={
                prodottiItemsSorted?.[0]?.pageLine?.relatedProduct?.titleRelated ||
                labelManagerLabels.relatedProducts
              }
            />
          )}
          <div className="pdpSliderBottom__buttons">
            <SwiperPrevBtn
              swiper={swiperData}
              ref={prevRef}
              hideRightArrow={hideRightArrow}
              hideLeftArrow={hideLeftArrow}
            />
            <SwiperNextBtn
              swiper={swiperData}
              ref={nextRef}
              hideRightArrow={hideRightArrow}
              hideLeftArrow={hideLeftArrow}
            />
          </div>
          <Swiper
            ref={setSlider}
            onSwiper={(swiper) => {
              setSwiperData(swiper);
            }}
            onInit={(swiper) => {
              swiper.navigation.prevEl = prevRef.current;
              swiper.navigation.nextEl = nextRef.current;
              swiper.navigation.update();
              setActiveIndex(swiper.isEnd + swiper.isBeginning);
              setHideRightArrow(swiper.isEnd);
              setHideLeftArrow(swiper.isBeginning);
            }}
            modules={[Pagination, A11y, Navigation]}
            className="products-slider"
            pagination={{ clickable: true }}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            watchSlidesProgress={true}
            centeredSlides={false}
            breakpoints={defaultBreakpoints}
            onTouchMove={(e) => {
              setHideRightArrow(e.isEnd);
              setHideLeftArrow(e.isBeginning);
            }}
            onPaginationUpdate={(e) => {
              setHideRightArrow(e.isEnd);
              setHideLeftArrow(e.isBeginning);
            }}
            onSlideChangeTransitionStart={(e) => {
              setHideRightArrow(e.isEnd);
              setHideLeftArrow(e.isBeginning);
            }}
          >
            {prodottiItemsSorted.map((product, idx) => (
              <SwiperSlide key={product.key_prod}>
                <ProductCard
                  index={idx}
                  productIds={compareIds}
                  toggleProduct={onToggleProduct}
                  origin={origin}
                  productNameVerbose={
                    product.productDescBefore
                      ? product.productDescBefore
                      : product.minicard4?.length
                      ? product.minicard4.slice(0, 5).join(", ")
                      : ""
                  }
                  isCompare={true}
                  originComponent={isAcCompare ? "Aec product - PDP" : "Related product - PDP"}
                  location={location}
                  madeProductLineId={madeProductLineId}
                  badge={badge}
                  bannerPromo={bannerPromo}
                  liferayProductNotes={liferayProductNotes}
                  extraServices={extraServices}
                  onHide={onHide}
                  {...product}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
    </>
  );
};

export default SliderRelated;
